import type { ReactNode } from "react";
import { MarkLast, ReadingPage, readingBody, readingEyebrow, readingH1, readingH2, readingSection, readingUpdated } from "@/components/public/reading-page";
import { canonicalLink, canonicalMeta, ogImageMeta } from "@/lib/seo";

export type LegalSection = { title: string; body: string | string[]; id?: string };

function paragraphs(body: string | string[]) {
  return Array.isArray(body) ? body : [body];
}

export function LegalBlock({ title, body, id, children, className = "reveal " + readingSection }: LegalSection & { children?: ReactNode; className?: string }) {
  return (
    <section id={id} className={className}>
      <h2 className={readingH2}>{title}</h2>
      {paragraphs(body).map((p, i) => (
        <p key={i} className={readingBody}>{p}</p>
      ))}
      {children}
    </section>
  );
}

/** Shared bilingual legal layout: Arabic first, English below on the same page. */
export function LegalPage({
  kicker,
  title,
  updatedAr,
  intro,
  sections,
  enTitle,
  updatedEn,
  enIntro,
  enSections,
  afterAr,
}: {
  kicker: string;
  title: string;
  updatedAr: string;
  intro?: ReactNode;
  sections: LegalSection[];
  enTitle: string;
  updatedEn: string;
  enIntro?: ReactNode;
  enSections: LegalSection[];
  afterAr?: ReactNode;
}) {
  return (
    <ReadingPage
      header={
        <>
          <p className={readingEyebrow}>{kicker}</p>
          <h1 className={readingH1}><MarkLast text={title} /></h1>
          <p className={readingUpdated}>آخر تحديث: {updatedAr}</p>
          {intro ? <div className={readingBody}>{intro}</div> : null}
        </>
      }
    >
      <div className="space-y-8">
        {sections.map((s, i) => (
          <LegalBlock key={s.title} {...s} className={(i < 3 ? "" : "reveal ") + readingSection} />
        ))}
        {afterAr}
      </div>

      <div dir="ltr" lang="en" className="reveal mt-16 border-t border-hairline-default pt-10 text-start">
        <h2 className="font-display text-[30px] font-extrabold text-text-primary">{enTitle}</h2>
        <p className={readingUpdated}>Last updated: {updatedEn}</p>
        {enIntro ? <div className={readingBody}>{enIntro}</div> : null}
        <div className="mt-8 space-y-8">
          {enSections.map((s) => (
            <LegalBlock key={s.title} {...s} />
          ))}
        </div>
      </div>
    </ReadingPage>
  );
}

/** head() for a trust page: unique title/description, self canonical, indexable. */
export function legalHead(path: string, title: string, description: string) {
  const full = `${title} — د. سارة النحّاس`;
  return {
    meta: [
      { title: full },
      { name: "description", content: description },
      { property: "og:title", content: full },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ar_SY" },
      { property: "og:site_name", content: "د. سارة النحّاس" },
      { name: "twitter:card", content: "summary_large_image" },
      ...ogImageMeta(),
      ...canonicalMeta(path),
    ],
    links: [canonicalLink(path)],
  };
}
